// =====================================================================
// ISOLATED taskboard mock — DEFAULT OFF.
//
// The redesign depends on additive backend endpoints (entry PATCH,
// append-only updates subresource, folder last_activity, soft-delete)
// that the SCHEMA-PLAN adds and task-builder implements. To build and
// verify the frontend UI-first, this module provides an in-memory
// stand-in for the whole taskboard data surface.
//
// It is OFF unless explicitly enabled, so the production path always
// hits the real API (services/api.js). Enable for local dev only:
//   localStorage.setItem("tp_taskboard_mock", "1")   // then reload
// or build with  REACT_APP_TASKBOARD_MOCK=1 .
//
// Nothing here is imported by the real API path; the facade
// (taskboardService.js) is the single switch point.
// =====================================================================

export function isMockEnabled() {
  try {
    if (typeof process !== "undefined" && process.env?.REACT_APP_TASKBOARD_MOCK === "1") return true;
    if (typeof window !== "undefined" && window.localStorage?.getItem("tp_taskboard_mock") === "1") return true;
  } catch {
    /* SSR / restricted storage — treat as off */
  }
  return false;
}

const nowIso = () => new Date().toISOString();
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString();
const uid = (() => {
  let i = 1000;
  return () => `m${i++}`;
})();

// --- seed data -------------------------------------------------------
function seed() {
  const folders = [
    mkFolder("f1", "Luca Ferretti", "Central midfield · CM", "Italy", "AC Reggiana", "Sofia Lindqvist", daysAgo(0)),
    mkFolder("f2", "Diego Marchetti", "Right winger · RW", "Argentina", "Boca Prieto", "Sofia Lindqvist", daysAgo(1)),
    mkFolder("f3", "Noah Andersson", "Centre-back · CB", "Sweden", "Malmö Nord", "Daniel Reyes", daysAgo(9)),
    mkFolder("f4", "Kwame Mensah", "Striker · ST", "Ghana", "Accra United", "Daniel Reyes", daysAgo(3)),
  ];
  const entries = {
    f1: [
      mkEntry("e1", "phone", "open", "Intro call with the sporting director — wants March match data before the next round.", team("Olympique Vareille", "France"), daysAgo(2), [
        mkUpdate("u1", "Left a voicemail, assistant will call back tomorrow.", "Sofia Lindqvist", daysAgo(1), true),
      ]),
      mkEntry("e2", "email", "open", "Player group chat — asked us for an updated highlight reel by Friday.", null, daysAgo(4)),
      mkEntry("e3", "meeting", "open", "Renewal-clause check with legal — flag anything blocking a summer move.", null, daysAgo(6)),
      mkEntry("e4", "phone", "waiting", "Verbal offer EUR 2.1M + bonuses. Awaiting written confirmation from the board.", team("SV Rheinfeld", "Germany"), daysAgo(9), [
        mkUpdate("u2", "Board meets Thursday, decision expected after.", "Sofia Lindqvist", daysAgo(3), false),
        mkUpdate("u3", "Nudged the DoF — still waiting.", "Sofia Lindqvist", daysAgo(1), false),
      ]),
      mkEntry("e5", "meeting", "waiting", "Recap sent after the in-person meeting — waiting on their shortlist decision.", team("Calcio Ternova", "Italy"), daysAgo(11)),
      mkEntry("e6", "meeting", "in_progress", "Their chief scout attends the U21 friendly on Jul 12 — tickets arranged.", team("Atletico Miravel", "Spain"), daysAgo(1)),
      mkEntry("e7", "phone", "in_progress", "Follow-up call — contract length flexible, wage question open.", team("Oostduin FC", "Netherlands"), daysAgo(2)),
      mkEntry("e8", "meeting", "accepted", "Season planning with player and family — priorities agreed.", null, daysAgo(15)),
      mkEntry("e9", "phone", "accepted", "First contact with head of recruitment — genuine interest for summer.", team("SV Rheinfeld", "Germany"), daysAgo(21), [
        mkUpdate("u4", "They asked for the medical summary.", "Sofia Lindqvist", daysAgo(18), false),
      ]),
    ],
    f2: [mkEntry("e20", "phone", "open", "Agent intro — gauging interest for a loan.", null, daysAgo(1))],
    f3: [mkEntry("e30", "email", "waiting", "Sent dossier, no reply in 9 days.", team("Malmö Nord", "Sweden"), daysAgo(9))],
    f4: [mkEntry("e40", "meeting", "in_progress", "Trial scheduled next week.", null, daysAgo(3))],
  };
  const links = {
    f1: [
      mkLink("l1", "https://youtu.be/highlights2026", "2025/26 highlight reel", "video", "Sofia Lindqvist", daysAgo(5)),
      mkLink("l2", "https://drive.google.com/file/d/abc/view", "Contract draft v2", "document", "Daniel Reyes", daysAgo(7)),
      mkLink("l3", "https://scouting.example.com/report/ferretti.pdf", "Independent scout report", "scout_report", "Daniel Reyes", daysAgo(12)),
      mkLink("l4", "https://transfermarkt.example/profile", "Market value profile", "other", "Sofia Lindqvist", daysAgo(20)),
    ],
    f2: [], f3: [], f4: [],
  };
  return { folders, entries, links };
}

function mkFolder(id, name, position, country, clubName, agentName, lastActivity) {
  return {
    id,
    player: { name, position, nationality: country, current_club: { name: clubName } },
    current_agent: { name: agentName, account_id: agentName.replace(/\s/g, "").toLowerCase() },
    last_activity_at: lastActivity,
  };
}
function team(name, country) {
  return { wyscout_team_id: Math.floor(Math.random() * 90000), name, country, logo: null };
}
const BOARD = { open: "open", waiting: "waiting", in_progress: "in_progress", done: "done", accepted: "done", rejected: "done" };
function mkEntry(id, mode, status, notes, clubTeam, date, updates = []) {
  return {
    id,
    mode_of_communication: mode,
    status, // raw (may be legacy)
    board_status: BOARD[status] || "open",
    notes,
    date: date.slice(0, 10),
    created_at: date,
    updated_at: date,
    country: clubTeam?.country || "",
    // Snapshot shape mirrors the backend contract.
    club: clubTeam ? "" : "",
    wyscout_team_id: clubTeam?.wyscout_team_id || null,
    club_name_snapshot: clubTeam?.name || "",
    club_logo_snapshot: clubTeam?.logo || null,
    club_snapshot: clubTeam ? { wyscout_team_id: clubTeam.wyscout_team_id, name: clubTeam.name, logo: clubTeam.logo } : null,
    agent: { name: "Sofia Lindqvist", account_id: "sofialindqvist" },
    updates,
  };
}
function mkUpdate(id, text, authorName, ts, pinned) {
  return { id, text, author: { name: authorName }, created_at: ts, pinned };
}
function mkLink(id, url, title, category, byName, ts) {
  return { id, url, title, category, description: "", created_by: { name: byName }, created_at: ts };
}

// Persist the mock across reloads within a session so drags/edits stick.
let DB = null;
function db() {
  if (DB) return DB;
  try {
    const raw = window.localStorage.getItem("tp_taskboard_mock_db");
    DB = raw ? JSON.parse(raw) : seed();
  } catch {
    DB = seed();
  }
  return DB;
}
function persist() {
  try {
    window.localStorage.setItem("tp_taskboard_mock_db", JSON.stringify(DB));
  } catch {
    /* ignore */
  }
}
const delay = (data) => new Promise((r) => setTimeout(() => r(data), 120));

// --- mock API (mirrors services/api.js return shapes) ----------------
export const mock = {
  async getFolders() {
    const { folders, entries } = db();
    const withCounts = folders.map((f) => ({
      ...f,
      entries_count: (entries[f.id] || []).length,
      links_count: (db().links[f.id] || []).length,
      entries: entries[f.id] || [],
    }));
    return delay({ success: true, data: withCounts });
  },
  async getFolderDetail(folderId) {
    const f = db().folders.find((x) => String(x.id) === String(folderId));
    return delay(f ? { success: true, data: f } : { success: false, status: 404, error: "Not found" });
  },
  async getEntries(folderId) {
    return delay({ success: true, data: db().entries[folderId] || [] });
  },
  async getLinks(folderId) {
    return delay({ success: true, data: db().links[folderId] || [] });
  },
  async createEntry(folderId, payload) {
    const clubTeam = payload.wyscout_team_id
      ? { name: payload.club_name_snapshot, country: payload.country || "", wyscout_team_id: payload.wyscout_team_id, logo: payload.club_logo_snapshot || null }
      : (payload.club ? { name: payload.club, wyscout_team_id: null, logo: null, country: "" } : null);
    const e = mkEntry(uid(), payload.mode_of_communication, payload.status || "open", payload.notes, clubTeam, nowIso());
    if (payload.club && !payload.wyscout_team_id) { e.club = payload.club; e.club_snapshot = null; }
    e.date = payload.date || e.date;
    (db().entries[folderId] = db().entries[folderId] || []).unshift(e);
    touch(folderId);
    persist();
    return delay({ success: true, data: e });
  },
  async patchEntry(folderId, entryId, payload) {
    const e = (db().entries[folderId] || []).find((x) => String(x.id) === String(entryId));
    if (!e) return delay({ success: false, status: 404, error: "Not found" });
    Object.assign(e, payload, { updated_at: nowIso() });
    if (payload.status) e.board_status = BOARD[payload.status] || "open";
    // Club input: picker fields, unlisted {club}, or clear (No club).
    if (payload.wyscout_team_id) {
      e.wyscout_team_id = payload.wyscout_team_id;
      e.club_name_snapshot = payload.club_name_snapshot || "";
      e.club_logo_snapshot = payload.club_logo_snapshot || null;
      e.club_snapshot = { wyscout_team_id: payload.wyscout_team_id, name: payload.club_name_snapshot, logo: payload.club_logo_snapshot || null };
      e.club = "";
    } else if (payload.club) {
      e.wyscout_team_id = null;
      e.club_snapshot = null;
      e.club_name_snapshot = payload.club;
      e.club = payload.club;
    } else if ("wyscout_team_id" in payload || "club" in payload) {
      e.wyscout_team_id = null;
      e.club_snapshot = null;
      e.club_name_snapshot = "";
      e.club = "";
    }
    touch(folderId);
    persist();
    return delay({ success: true, data: e });
  },
  async deleteEntry(folderId, entryId) {
    // Soft-delete: status transition, keep the row (house rule).
    const e = (db().entries[folderId] || []).find((x) => String(x.id) === String(entryId));
    if (e) { e.deleted = true; e.updated_at = nowIso(); }
    db().entries[folderId] = (db().entries[folderId] || []).filter((x) => !x.deleted);
    persist();
    return delay({ success: true });
  },
  async addUpdate(folderId, entryId, payload) {
    const e = (db().entries[folderId] || []).find((x) => String(x.id) === String(entryId));
    if (!e) return delay({ success: false, status: 404, error: "Not found" });
    const u = mkUpdate(uid(), payload.text, { name: "You" }.name ? { name: "You" } : null, nowIso(), false);
    u.author = { name: "You" };
    e.updates = e.updates || [];
    e.updates.push(u);
    e.updated_at = nowIso();
    touch(folderId);
    persist();
    return delay({ success: true, data: u });
  },
  async pinUpdate(folderId, entryId, updateId, pinned) {
    const e = (db().entries[folderId] || []).find((x) => String(x.id) === String(entryId));
    if (!e) return delay({ success: false, status: 404, error: "Not found" });
    (e.updates || []).forEach((u) => { u.pinned = pinned && String(u.id) === String(updateId); });
    persist();
    return delay({ success: true, data: (e.updates || []).find((u) => String(u.id) === String(updateId)) });
  },
  async createLink(folderId, payload) {
    const l = mkLink(uid(), payload.url, payload.title || payload.url, payload.category, "You", nowIso());
    (db().links[folderId] = db().links[folderId] || []).unshift(l);
    touch(folderId);
    persist();
    return delay({ success: true, data: l });
  },
  async updateLink(folderId, linkId, payload) {
    const l = (db().links[folderId] || []).find((x) => String(x.id) === String(linkId));
    if (l) Object.assign(l, payload);
    persist();
    return delay({ success: true, data: l });
  },
  async deleteLink(folderId, linkId) {
    db().links[folderId] = (db().links[folderId] || []).filter((x) => String(x.id) !== String(linkId));
    persist();
    return delay({ success: true });
  },
  async getRosterPlayers() {
    // Roster for the Create-folder picker: some players already have a
    // folder (match the seeded folders by name) so the duplicate-block
    // "Folder exists — open it" affordance shows; others do not.
    const roster = [
      { id: "p1", name: "Luca Ferretti", position: "CM", current_club: { name: "AC Reggiana" } },
      { id: "p2", name: "Diego Marchetti", position: "RW", current_club: { name: "Boca Prieto" } },
      { id: "p3", name: "Ana Sørensen", position: "GK", current_club: { name: "Odense BK" } },
      { id: "p4", name: "Marco Silva", position: "LB", current_club: { name: "Braga B" } },
      { id: "p5", name: "Noah Andersson", position: "CB", current_club: { name: "Malmö Nord" } },
    ];
    return delay({ data: { data: roster } });
  },
  async searchTeams(q) {
    const pool = [
      { wyscout_team_id: 1, name: "Olympique Vareille", country: "France", image_url: null },
      { wyscout_team_id: 2, name: "SV Rheinfeld", country: "Germany", image_url: null },
      { wyscout_team_id: 3, name: "Calcio Ternova", country: "Italy", image_url: null },
      { wyscout_team_id: 4, name: "Atletico Miravel", country: "Spain", image_url: null },
      { wyscout_team_id: 5, name: "Oostduin FC", country: "Netherlands", image_url: null },
      { wyscout_team_id: 6, name: "Malmö Nord", country: "Sweden", image_url: null },
    ];
    const needle = (q || "").toLowerCase();
    return delay({ success: true, data: pool.filter((t) => t.name.toLowerCase().includes(needle)) });
  },
};

function touch(folderId) {
  const f = db().folders.find((x) => String(x.id) === String(folderId));
  if (f) f.last_activity_at = nowIso();
}
